import { REQUEST_PREFIXES } from '../pack/lexicons';
import { type FeatureContext } from '../types';
import { stripLeadingPhrase } from './cues';
import { hasOperandEvidence } from './operand-evidence';

/**
 * The shared shape of a first-semantic-position cue family.
 *
 * `C-ACTION`, `C-EXPLAIN`, `C-RETRIEVE`, and the request variants differ only in their
 * cue lexicon and their exclusions — the surrounding contract is identical: at most one
 * request prefix, the cue at the first semantic position, and operand evidence after it.
 *
 * This is the one predicate in the pack that is genuinely shared. Copying it four times
 * would let the families drift apart in exactly the places that matter for precision, so
 * it lives here and each matcher supplies only its own lexicon.
 *
 * A negation in front of a cue needs no special handling: it occupies the first semantic
 * position, so `don't delete the page` fails the position test and never reaches the
 * operand test.
 */

export type CueFamilyOptions = {
	/** The family's cue lexicon. Longest match wins. */
	cues: readonly string[];
	/** Spans that keep the query on the residual path even though a cue leads it. */
	exclusions?: readonly string[];
	/** Whether one leading request prefix is permitted before the cue. */
	allowRequestPrefix?: boolean;
};

export type CueFamilyMatch = {
	/** Text after the cue. */
	span: string;
	/** True when a request prefix was stripped. */
	hasRequestPrefix: boolean;
	/** The cue that matched. */
	cue: string;
};

/**
 * Resolves the cue position for a family, or `null` when the family does not apply.
 *
 * A bare cue and a deictic-only operand (`explain`, `explain this`) both resolve to
 * `null`: they name nothing to act on, and the rules below route them to Search.
 */
export const resolveCueFamily = (
	features: FeatureContext,
	{ cues, exclusions = [], allowRequestPrefix = true }: CueFamilyOptions,
): CueFamilyMatch | null => {
	const qMatch = features.qMatch;

	// Exclusions are checked against the whole query so an excluded span cannot be
	// smuggled in behind a request prefix.
	if (exclusions.some((exclusion) => stripLeadingPhrase(qMatch, [exclusion]) !== null)) {
		return null;
	}

	const prefix = allowRequestPrefix ? stripLeadingPhrase(qMatch, REQUEST_PREFIXES) : null;
	const cue = stripLeadingPhrase(prefix ? prefix.rest : qMatch, cues);

	if (!cue || !hasOperandEvidence(cue.rest)) {
		return null;
	}

	return { span: cue.rest, hasRequestPrefix: prefix !== null, cue: cue.phrase };
};
